import { SourcingOption } from "../constants";
import { fetchImageForJSPDF, sanitizeAndConvertText } from "../functions";
import { DataTable, Dimension, PageContent } from "../types";
import { buildDataSheetHTML } from "./datasheet";

export const buildMaterialHTML = async (
  data: PageContent,
  showBasicDetails: boolean,
  showDataSheet: boolean,
  imageDimension?: Dimension
) => {
  let body = `<div class="page-break relative p-4 w-full">`;

  if (showBasicDetails) {
    const sourcingType =
      SourcingOption.find(
        (d) =>
          d?.value === (data?.sourcing_type || data?.material?.sourcing_type)
      )?.label || "";

    body += `
      <p class="text-2xl font-bold">Basic Details</p>
    `;

    let tableData = [
      {
        title: "Material ID & Revision Number",
        content: `${
          data?.sequence_id || data?.material?.sequence_id || "..."
        }-R${data?.version || data?.material?.version || 0}`,
      },
      {
        title: "Material Short Description",
        content: `${
          data?.short_description || data?.material?.short_description || "---"
        }`,
      },
    ];

    let group_name = data?.group_name || data?.material?.group_name;

    if (group_name) {
      tableData.push({
        title: "Material Group",
        content: group_name,
      });
    }

    let noun_name = data?.noun_name || data?.material?.noun_name;

    if (noun_name) {
      tableData.push({
        title: "Material Tag",
        content: noun_name,
      });
    }

    let reference_id = data?.external_ref_id || data?.material?.external_ref_id;

    if (reference_id) {
      tableData.push({
        title: "Reference Number",
        content: reference_id,
      });
    }

    let uom = data?.uom || data?.material?.uom;

    if (uom) {
      tableData.push({
        title: "UOM",
        content: uom,
      });
    }

    if (sourcingType) {
      tableData.push({
        title: "Sourcing Type",
        content: sourcingType,
      });
    }

    let vendor_name =
      data?.vendor?.vendor_name || data?.material?.vendor?.vendor_name;

    if (vendor_name) {
      tableData.push({
        title: "Supplier Name",
        content: vendor_name,
      });
    }

    let manufacturer_name =
      data?.vendor?.manufacturer_name ||
      data?.material?.vendor?.manufacturer_name;

    if (manufacturer_name) {
      tableData.push({
        title: "Manufacturer Name",
        content: manufacturer_name,
      });
    }

    let part_number =
      data?.vendor?.part_number || data?.material?.vendor?.part_number;

    if (part_number) {
      tableData.push({
        title: "Manufacturer Number",
        content: part_number,
      });
    }

    let unit_code =
      data?.organization_unit?.unit_code ||
      data?.material?.organization_unit?.unit_code;

    if (unit_code) {
      tableData.push({
        title: "Unit Name",
        content: unit_code,
      });
    }

    let unit_location =
      data?.organization_unit?.unit_location ||
      data?.material?.organization_unit?.unit_location;

    if (unit_location) {
      tableData.push({
        title: "Unit Location",
        content: unit_location,
      });
    }

    let other_details =
      data?.organization_unit?.other_details ||
      data?.material?.organization_unit?.other_details;

    if (other_details) {
      tableData.push({
        title: "Other Details",
        content: other_details,
      });
    }

    let serial_num = data?.serial_num || data?.material?.serial_num;

    if (serial_num) {
      tableData.push({
        title: "Tag Number",
        content: serial_num,
      });
    }

    let normal_quantity =
      data?.normal_quantity || data?.material?.normal_quantity;

    if (typeof normal_quantity === "number") {
      tableData.push({
        title: "Default Quantity",
        content: String(normal_quantity),
      });
    }

    let spare_part =
      data?.spare_part || data?.material?.spare_part ? "Yes" : "No";

    if (typeof (data?.spare_part || data?.material?.spare_part) === "boolean") {
      tableData.push({
        title: "Spare Part",
        content: spare_part,
      });
    }

    let tagsInfo = data?.tagsInfo || data?.material?.tagsInfo;

    let spare_part_category = tagsInfo?.find(
      (tag) => tag.tag_group_canonical_name === "sp_category"
    )?.tag_name;

    if (spare_part_category) {
      tableData.push({
        title: "Spare Part Category",
        content: spare_part_category,
      });
    }

    let spare_part_quantity =
      data?.spare_part_quantity || data?.material?.spare_part_quantity;

    if (typeof spare_part_quantity === "number") {
      tableData.push({
        title: "Spare Part Quantity",
        content: String(spare_part_quantity),
      });
    }

    let material_status = tagsInfo?.find(
      (tag) => tag.tag_group_canonical_name === "child_mat_status"
    )?.tag_name;

    if (material_status) {
      tableData.push({
        title: "Material Status",
        content: material_status,
      });
    }

    const notes = sanitizeAndConvertText(
      data?.material?.notes || data?.notes || ""
    );

    if (notes) {
      tableData.push({
        title: "Notes",
        content: notes,
      });
    }

    body += `<div class="relative pt-2 w-full">
                    <table class="table-fixed w-full mt-5 pl-6">
                        <thead>
                            <tr class="bg-[#d1c4e9] text-[#000000]">
                                <th class="pl-2 py-2 text-md text-left break-all w-[35%]">Title</th>
                                <th class="pl-2 py-2 text-md text-left break-all w-[65%]">Content</th>
                            </tr>
                        </thead>
                        <tbody>

                            ${
                              tableData
                                ?.map((td, idx) => {
                                  return `
                                <tr class="${
                                  idx % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                                }">
                                    <td class="px-2 py-2 text-md text-left break-all w-[35%]">${
                                      td.title
                                    }</td>
                                    <td class="px-2 py-2 text-md text-left break-all w-[65%]">${
                                      td.content
                                    }</td>
                                </tr>
                        `;
                                })
                                ?.join("") ?? ""
                            }
                        </tbody>
                    </table>
        </div>`;

    const attachments = data?.attachments || data?.material?.attachments || [];

    if (attachments.length > 0) {
      const { width = 128, height = 128 } = imageDimension;

      let contentUrl = attachments[attachments.length - 1]?.url;

      body += `<p class="mt-8 text-2xl font-bold">Material Image</p>`;

      await fetchImageForJSPDF(contentUrl)
        .then((contentBytes: string) => {
          body += `<img class="mt-2" src="${contentBytes}" alt="Material Image" style="max-width: ${width}px; max-height: ${height}px; width: auto; height: auto; object-fit: contain; display: block; margin: auto;" />
      `;
        })
        .catch(async (error: any) => {
          console.error(`Error loading content image on preview: ${error}`);
        });
    }
  }

  if (showDataSheet) {
    const dataSheets = data?.datasheets || data?.material?.datasheets || [];

    const dataSheet = (dataSheets?.[dataSheets?.length - 1] || {}) as DataTable;

    if (dataSheet) {
      body += await buildDataSheetHTML(dataSheet);
    }
  }

  body += `</div>`;

  return Promise.resolve(body);
};
